/**
 * Brand logo badge shown at the top-right of a game card and a camp card.
 *
 * Files live in `public/provider-badges/<CODE>.png`, 256x96 (drawn 64x24), with
 * the plate baked in. Source files, rules and per-logo notes:
 * `docs/assets/provider-logos/` and `docs/game/provider-brand-badges.md`.
 */

/** Badge files shipped in `public/provider-badges/`. */
const BADGE_CODES: ReadonlySet<string> = new Set([
  'ALLBET', 'CQ9', 'DREAMGAMING', 'ENDORPHINA', 'EVOLUTION', 'EVOPLAY', 'IBEX', 'JILI', 'JOKER', 'KINGMAKER',
  'MICROGAMING', 'NEXTSPIN', 'PGSOFT', 'PRAGMATIC', 'QTECH', 'RICH88', 'RSG', 'SAGAMING', 'SBOBET', 'SEXY',
  'SIMPLEPLAY', 'SUPERM', 'SUPERM-LOTTO', 'WINSLOT', 'WMCASINO', 'WSGAMING',
]);

/**
 * Upstream brand code (`brandCode` on a catalog game: a Gamesway sub-provider,
 * an AMB product or a SUPERM sub-provider) to the badge file it shows. A brand
 * with no entry has no badge yet.
 */
const BRAND_TO_BADGE: Readonly<Record<string, string>> = {
  AMBPGSLOT: 'PGSOFT', PGSLOT: 'PGSOFT', PGSOFT: 'PGSOFT',
  JILI: 'JILI', JOKER: 'JOKER', NEXTSPIN: 'NEXTSPIN', MICRO: 'MICROGAMING', QTECH: 'QTECH', IBEX: 'IBEX',
  EVOPLAY: 'EVOPLAY', RSGGAMING: 'RSG', WINSLOT: 'WINSLOT', PRAGMATIC: 'PRAGMATIC', KINGMAKER: 'KINGMAKER',
  RICH88: 'RICH88', CQGAME: 'CQ9', SIMPLEPLAY: 'SIMPLEPLAY', DREAMGAME: 'DREAMGAMING', WSGAMING: 'WSGAMING',
  EVOLUTION: 'EVOLUTION', WMGAMING: 'WMCASINO', SEXY: 'SEXY', ALLBET: 'ALLBET', SAGAME: 'SAGAMING', SBOBET: 'SBOBET',
  'SUPERM-LOTTO': 'SUPERM-LOTTO',
};

/** Camp (provider row) code to its badge, for camps that are one brand and for the SUPERM aggregator. */
const PROVIDER_TO_BADGE: Readonly<Record<string, string>> = {
  pgsoft: 'PGSOFT', 'amb-pg': 'PGSOFT', 'pglaz-pg': 'PGSOFT', 'awc-jili': 'JILI', awc: 'SEXY', endorphina: 'ENDORPHINA', wm: 'WMCASINO',
  superm: 'SUPERM', 'SUPERM-LOTTO': 'SUPERM-LOTTO', 'SUPERM-JOKER': 'JOKER',
};

function known(code: string | undefined): string | null {
  return code && BADGE_CODES.has(code) ? code : null;
}

/** Badge code for a game: its own brand first, else its camp's. */
export function badgeCodeOf(game: { brandCode?: string | null; providerCode?: string | null }): string | null {
  const brand = game.brandCode ? known(BRAND_TO_BADGE[game.brandCode.toUpperCase()]) : null;
  if (brand) return brand;
  return providerBadgeCodeOf(game.providerCode);
}

/** Badge code for a camp card. */
export function providerBadgeCodeOf(providerCode: string | null | undefined): string | null {
  return providerCode ? known(PROVIDER_TO_BADGE[providerCode]) : null;
}

export function badgeSrc(code: string): string {
  return `/provider-badges/${code}.png`;
}
